import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SubjectIcon from "@mui/icons-material/Subject";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HistoryIcon from "@mui/icons-material/History";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";
import StatusChip from "../common/StatusChip";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const formatDate = (date) =>
  new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const OvertimeRequest = () => {
  const [formData, setFormData] = useState({
    workDate: "",
    startTime: "",
    endTime: "",
    reason: "",
  });
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const todayDate = getTodayDateString();

  const fetchRequests = async () => {
    try {
      setFetching(true);
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${BASE_URL}/api/overtime/my-requests`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch overtime requests");
      }

      setRequests(data || []);
    } catch (err) {
      setError(err.message || "Failed to fetch overtime requests");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    try {
      if (formData.workDate && formData.workDate > todayDate) {
        throw new Error("Future date is not allowed for overtime request");
      }

      const token = sessionStorage.getItem("token");
      const response = await fetch(`${BASE_URL}/api/overtime/requests`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to submit overtime request");
      }

      setMessage(data.message || "Overtime request submitted successfully");
      setFormData({
        workDate: "",
        startTime: "",
        endTime: "",
        reason: "",
      });
      fetchRequests();
    } catch (err) {
      setError(err.message || "Failed to submit overtime request");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBorderColor = (status) => {
    const s = status?.toLowerCase();
    if (s === "approved") return "#43e97b";
    if (s === "rejected") return "#f5576c";
    return "#f7971e";
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#f5f7fb",
      fontSize: "0.875rem",
      "& fieldset": { borderColor: "#eef0f6" },
      "&:hover fieldset": { borderColor: "#CBD5E1" },
      "&.Mui-focused fieldset": { borderColor: "#667eea" },
    },
    "& .MuiInputLabel-root": { fontSize: "0.875rem" },
  };

  return (
    <PageContainer>
      {/* Gradient Page Header */}
      <PageHeader
        title="Overtime Management"
        subtitle="Submit overtime requests and track their approval status"
        icon={<MoreTimeIcon />}
        gradient
      />

      <Grid container spacing={3}>
        {/* Left: Request Form */}
        <Grid item xs={12} md={5}>
          <Paper
            component="form"
            onSubmit={handleSubmit}
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid #eef0f6",
              overflow: "hidden",
              boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
            }}
          >
            {/* Gradient accent header strip */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                px: 3,
                py: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  bgcolor: "rgba(255,255,255,0.18)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AccessTimeIcon sx={{ fontSize: 18, color: "#fff" }} />
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", color: "#fff" }}>
                Request Overtime
              </Typography>
            </Box>

            <Box sx={{ p: 3 }}>
              <Stack spacing={2.5}>
                {/* Work Date with icon label */}
                <Box>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#5a5f7a", mb: 0.75, display: "flex", alignItems: "center", gap: 0.75 }}>
                    <CalendarTodayIcon sx={{ fontSize: 14, color: "#667eea" }} />
                    Work Date <span style={{ color: "#DC2626" }}>*</span>
                  </Typography>
                  <TextField
                    type="date"
                    name="workDate"
                    value={formData.workDate}
                    onChange={handleChange}
                    inputProps={{ max: todayDate }}
                    InputLabelProps={{ shrink: true }}
                    required
                    fullWidth
                    size="small"
                    sx={fieldSx}
                  />
                </Box>

                {/* Time Fields */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#5a5f7a", mb: 0.75, display: "flex", alignItems: "center", gap: 0.75 }}>
                      <ScheduleIcon sx={{ fontSize: 14, color: "#667eea" }} />
                      Start Time <span style={{ color: "#DC2626" }}>*</span>
                    </Typography>
                    <TextField
                      type="time"
                      name="startTime"
                      value={formData.startTime}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      required
                      fullWidth
                      size="small"
                      sx={fieldSx}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#5a5f7a", mb: 0.75, display: "flex", alignItems: "center", gap: 0.75 }}>
                      <ScheduleIcon sx={{ fontSize: 14, color: "#667eea" }} />
                      End Time <span style={{ color: "#DC2626" }}>*</span>
                    </Typography>
                    <TextField
                      type="time"
                      name="endTime"
                      value={formData.endTime}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      required
                      fullWidth
                      size="small"
                      sx={fieldSx}
                    />
                  </Box>
                </Box>

                {/* Reason */}
                <Box>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#5a5f7a", mb: 0.75, display: "flex", alignItems: "center", gap: 0.75 }}>
                    <SubjectIcon sx={{ fontSize: 14, color: "#667eea" }} />
                    Reason <span style={{ color: "#DC2626" }}>*</span>
                  </Typography>
                  <TextField
                    name="reason"
                    value={formData.reason}
                    onChange={handleChange}
                    required
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    placeholder="Describe the overtime work performed..."
                    sx={fieldSx}
                  />
                </Box>

                {/* Submit Button - Gradient Pill */}
                <Button
                  type="submit"
                  disabled={loading}
                  fullWidth
                  endIcon={<ArrowForwardIcon sx={{ fontSize: "18px !important" }} />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "12px",
                    px: 3,
                    py: 1.5,
                    fontSize: "0.9rem",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "#fff",
                    boxShadow: "0 4px 16px rgba(102, 126, 234, 0.3)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)",
                      boxShadow: "0 6px 24px rgba(102, 126, 234, 0.45)",
                      transform: "translateY(-1px)",
                    },
                    "&:disabled": {
                      background: "#CBD5E1",
                      color: "#fff",
                      boxShadow: "none",
                    },
                  }}
                >
                  {loading ? "Submitting..." : "Submit Request"}
                </Button>

                {message && (
                  <Alert severity="success" sx={{ borderRadius: "12px", border: "1px solid #BBF7D0" }} onClose={() => setMessage("")}>
                    {message}
                  </Alert>
                )}
                {error && (
                  <Alert severity="error" sx={{ borderRadius: "12px", border: "1px solid #FECACA" }} onClose={() => setError("")}>
                    {error}
                  </Alert>
                )}
              </Stack>
            </Box>
          </Paper>
        </Grid>

        {/* Right: Requests Timeline */}
        <Grid item xs={12} md={7}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid #eef0f6",
              overflow: "hidden",
              boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                px: 3,
                py: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    bgcolor: "rgba(255,255,255,0.18)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <HistoryIcon sx={{ fontSize: 18, color: "#fff" }} />
                </Box>
                <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", color: "#fff" }}>
                  My Overtime Requests
                </Typography>
              </Box>
              {requests.length > 0 && (
                <Box
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    borderRadius: "8px",
                    px: 1.5,
                    py: 0.25,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "#fff",
                  }}
                >
                  {requests.length} request{requests.length !== 1 ? "s" : ""}
                </Box>
              )}
            </Box>

            {fetching ? (
              <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                <Typography sx={{ color: "#8b8fa3", fontSize: "0.875rem" }}>
                  Loading overtime requests...
                </Typography>
              </Box>
            ) : requests.length === 0 ? (
              <Box sx={{ py: 6, px: 3, textAlign: "center" }}>
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #eef0f6, #e8eaf6)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  <HourglassEmptyIcon sx={{ fontSize: 32, color: "#8b8fa3" }} />
                </Box>
                <Typography sx={{ fontWeight: 600, color: "#1a1a2e", mb: 0.5, fontSize: "1rem" }}>
                  No overtime requests
                </Typography>
                <Typography sx={{ color: "#8b8fa3", fontSize: "0.85rem", maxWidth: 280, mx: "auto" }}>
                  Submit your first overtime request using the form
                </Typography>
              </Box>
            ) : (
              <>
                {/* Desktop Table */}
                <TableContainer sx={{ display: { xs: "none", md: "block" }, overflowX: "auto", maxWidth: "100%" }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {["Date", "Time", "Hours", "Reason", "Status", "Manager Remark"].map(
                          (header) => (
                            <TableCell
                              key={header}
                              sx={{
                                background: "linear-gradient(180deg, #f8f9fc 0%, #f0f2f8 100%)",
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                color: "#5a5f7a",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                py: 1.5,
                                borderBottom: "2px solid #eef0f6",
                              }}
                            >
                              {header}
                            </TableCell>
                          )
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {requests.map((request) => (
                        <TableRow
                          key={request._id}
                          sx={{
                            borderLeft: `4px solid ${getStatusBorderColor(request.status)}`,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: "#f8f9fc",
                            },
                          }}
                        >
                          <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", py: 2, fontWeight: 500 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: "8px",
                                  bgcolor: "#f5f7fb",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: "#667eea", lineHeight: 1, textTransform: "uppercase" }}>
                                  {new Date(request.workDate).toLocaleDateString("en-US", { month: "short" })}
                                </Typography>
                                <Typography sx={{ fontSize: "0.85rem", fontWeight: 800, color: "#1a1a2e", lineHeight: 1 }}>
                                  {new Date(request.workDate).getDate()}
                                </Typography>
                              </Box>
                              <Typography sx={{ fontSize: "0.8rem", color: "#8b8fa3" }}>
                                {formatDate(request.workDate)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", py: 2 }}>
                            {request.startTime} - {request.endTime}
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Box
                              sx={{
                                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                color: "#fff",
                                borderRadius: "20px",
                                px: 1.5,
                                py: 0.25,
                                fontWeight: 700,
                                fontSize: "0.8rem",
                                display: "inline-block",
                                textAlign: "center",
                                minWidth: 40,
                              }}
                            >
                              {request.totalHours}h
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.85rem", color: "#5a5f7a", py: 2, maxWidth: 180 }}>
                            <Typography sx={{ fontSize: "0.85rem", color: "#5a5f7a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {request.reason}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <StatusChip status={request.status} />
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.85rem", color: "#8b8fa3", py: 2 }}>
                            {request.managerRemark || "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Mobile Cards - Colored Status Timeline */}
                <Box sx={{ display: { xs: "block", md: "none" }, p: 2 }}>
                  <Stack spacing={2}>
                    {requests.map((request) => {
                      const borderColor = getStatusBorderColor(request.status);
                      return (
                        <Paper
                          key={request._id}
                          elevation={0}
                          sx={{
                            borderRadius: "14px",
                            border: "1px solid #eef0f6",
                            borderLeft: `4px solid ${borderColor}`,
                            overflow: "hidden",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              boxShadow: "0 4px 14px rgba(0,0,0,0.07)",
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          <Box sx={{ px: 2.5, py: 2, bgcolor: "#f8f9fc", borderBottom: "1px solid #eef0f6" }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                {/* Date Badge */}
                                <Box
                                  sx={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: "10px",
                                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                  }}
                                >
                                  <Typography sx={{ fontSize: "0.55rem", fontWeight: 700, color: "rgba(255,255,255,0.8)", lineHeight: 1, textTransform: "uppercase" }}>
                                    {new Date(request.workDate).toLocaleDateString("en-US", { month: "short" })}
                                  </Typography>
                                  <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: "#fff", lineHeight: 1.1 }}>
                                    {new Date(request.workDate).getDate()}
                                  </Typography>
                                </Box>
                                <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a1a2e" }}>
                                  {formatDate(request.workDate)}
                                </Typography>
                              </Box>
                              <StatusChip status={request.status} />
                            </Stack>
                          </Box>
                          <Box sx={{ p: 2.5 }}>
                            <Stack spacing={1.5}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3" }}>Time</Typography>
                                <Typography sx={{ fontSize: "0.875rem", color: "#1a1a2e", fontWeight: 500 }}>
                                  {request.startTime} - {request.endTime}
                                </Typography>
                              </Box>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3" }}>Hours</Typography>
                                <Box
                                  sx={{
                                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                    color: "#fff",
                                    borderRadius: "20px",
                                    px: 1.5,
                                    py: 0.25,
                                    fontWeight: 700,
                                    fontSize: "0.8rem",
                                  }}
                                >
                                  {request.totalHours}h
                                </Box>
                              </Box>
                              <Box>
                                <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3", mb: 0.5 }}>
                                  Reason
                                </Typography>
                                <Typography sx={{ fontSize: "0.875rem", color: "#1a1a2e" }}>
                                  {request.reason}
                                </Typography>
                              </Box>
                              {request.managerRemark && (
                                <Box>
                                  <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3", mb: 0.5 }}>
                                    Manager Remark
                                  </Typography>
                                  <Typography sx={{ fontSize: "0.875rem", color: "#1a1a2e" }}>
                                    {request.managerRemark}
                                  </Typography>
                                </Box>
                              )}
                            </Stack>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Stack>
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default OvertimeRequest;
